import React, { useState, useEffect } from "react";
import logo from "./TyrolitLogo.png";
import "./MaterialPage.css";
import { API } from "aws-amplify";
import { useParams } from "react-router-dom";
import {
  listUsers,
  getCustomer,
  listMaterials,
  listBookedItems,
} from "./graphql/queries";
import * as mutations from "./graphql/mutations";

import {
  withAuthenticator,
  Button,
  Heading,
  Image,
  Card,
} from "@aws-amplify/ui-react";
import { useAuthenticator } from "@aws-amplify/ui-react";

const MaterialPage = ({ signOut }) => {
  const [customer, setCustomer] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [isStockEditor, setIsStockEditor] = useState(false);
  const [materials, setMaterials] = useState(null);
  const [bookingValue, setBookingValue] = useState("1");
  const [isBookingIn, setIsBookingIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuthenticator();
  const { searchTerm } = useParams();

  useEffect(() => {
    // Extract user groups from Cognito token
    if (user) {
      const groups = user?.signInUserSession?.accessToken?.payload?.["cognito:groups"] || [];
      setUserGroups(groups);
      const hasStockEditorAccess = groups.includes("StockEditors");
      setIsStockEditor(hasStockEditorAccess);
      
      if (hasStockEditorAccess) {
        // StockEditors have full access - set a default customer
        setCustomer({
          id: "admin",
          Company: "Stock Editors (Full Access)",
          Rep: user?.attributes?.name || user?.attributes?.email || "",
        });
      } else if (groups.length > 0) {
        // Regular customer group access - pass all groups
        fetchCustomerByGroup(groups);
      }
    }
  }, [user]);

  // Validate and sanitize input
  function validateBookingValue(value) {
    const num = parseInt(value, 10);
    if (!Number.isFinite(num) || num <= 0) return null;
    if (num > 10000) return null; // Max limit to prevent abuse
    return num;
  }

  function sanitizeSearchTerm(term) {
    if (!term || typeof term !== 'string') return null;
    const sanitized = term.trim().slice(0, 100); // Limit length
    if (!sanitized || sanitized.length === 0) return null;
    return sanitized;
  }

  useEffect(() => {
    if (customer && searchTerm) {
      searchMaterial(searchTerm);
    }
  }, [customer, searchTerm]);

  async function fetchCustomerByGroup(groupNames) {
    try {
      // Set customer to show all groups user is part of
      setCustomer({
        id: groupNames.join(","),
        Company: groupNames.join(", "),
        Rep: user?.attributes?.name || user?.attributes?.email || "",
      });
    } catch (err) {
      console.error(err);
    }
  }

  async function searchMaterial(term) {
    try {
      setError(null);
      setLoading(true);
      
      const sanitizedTerm = sanitizeSearchTerm(term);
      if (!sanitizedTerm) {
        setError("Invalid material search term");
        setLoading(false);
        return;
      }
      
      let all = [];
      let token = null;
      do {
        const res = await API.graphql({
          query: listMaterials,
          variables: { nextToken: token },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });
        all = all.concat(res.data.listMaterials.items);
        token = res.data.listMaterials.nextToken;
      } while (token);
      
      // Find material by tool number
      let foundMaterial = all.find((m) => m.ToolNumber === sanitizedTerm);
      
      if (!foundMaterial) {
        setError("Material not found");
        setLoading(false);
        return;
      }
      
      // Check access: StockEditors see all, others check their groups
      let hasAccess;
      if (isStockEditor) {
        hasAccess = true; // StockEditors have full access
      } else {
        hasAccess = userGroups.includes(foundMaterial.materialCustomerId);
      }
      
      if (hasAccess) {
        setMaterials(foundMaterial);
      } else {
        setError("You do not have access to this material");
      }
      setLoading(false);
    } catch (err) {
      setError("Error loading material. Please try again.");
      setLoading(false);
    }
  }

  async function updateAMounts(id, shelfActual, onOrder) {
    try {
      // Authorization: Allow booking operations for users with access to the material
      // Only StockEditors can directly edit via the stock page, but any authorized user can book
      const input = { id, Shelf_Level_Actual: shelfActual };
      if (onOrder != null) input.On_Order = onOrder;
      await API.graphql({
        query: mutations.updateMaterial,
        variables: { input },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
    } catch (err) {
      console.error("Update amounts error:", err);
      throw new Error("Failed to update material amounts: " + (err?.message || "Unknown error"));
    }
  }

  async function getUsersByCustomerId() {
    try {
      // Return email for notifications (could be enhanced to get all users in the customer group)
      return [user.attributes.email];
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  async function handleBooking(bookIn = false) {
    setError(null);
    
    // Validate booking value
    const validatedValue = validateBookingValue(bookingValue);
    if (!validatedValue) {
      setError("Please enter a valid quantity (1-10000)");
      return;
    }
    
    let val = validatedValue;
    
    // If booking in, make it negative
    if (bookIn) {
      val = Math.abs(val) * -1;
    } else {
      val = Math.abs(val);
    }
    
    const clock = prompt("Please enter your clock number:");
    if (!clock || clock.trim().length === 0) {
      setError("Clock number is required");
      return;
    }

    const action = bookIn ? "booking in" : "booking out";
    const qty = Math.abs(val);
    const ok = window.confirm(
      `You are ${action} ${qty} item${qty > 1 ? "s" : ""}. Continue?`
    );
    if (!ok) return;

    setLoading(true);
    try {
      const newShelf = materials.Shelf_Level_Actual - val;
      let newOnOrder = materials.On_Order || 0;
      // Decrease On_Order for both booking in and booking out
      newOnOrder -= Math.abs(val);

      console.log("Updating amounts:", { id: materials.id, newShelf, newOnOrder });
      await updateAMounts(materials.id, newShelf, newOnOrder);
      
      console.log("Creating booked item with:", {
        Amount: Math.abs(val),
        GoodsIN: bookIn,
        ToolNumber: materials.ToolNumber,
        CustomerName: customer.Company,
      });
      await API.graphql({
        query: mutations.createBookedItems,
        variables: {
          input: {
            Amount: Math.abs(val),
            GoodsIN: bookIn,
            EmailList: await getUsersByCustomerId(),
            ToolNumber: materials.ToolNumber,
            CustomerName: customer.Company,
            StorageLocation: materials.StorageLocation,
            Rep: customer.Rep,
            Notes: `clock number: ${clock.trim()}`,
            ToolNumberID: materials.id,
            CustomerNameID: customer.id,
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      
      // Update local state instead of reloading page
      setMaterials({
        ...materials,
        Shelf_Level_Actual: newShelf,
        On_Order: newOnOrder,
      });
      
      setBookingValue("1");
      setError(null);
      // Show success feedback
      alert(`Successfully booked ${qty} item(s)`);
    } catch (err) {
      console.error("Booking error:", err);
      setError(err?.message || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="MaterialPage">
      <div className="full-screen-container">
        <Image src={logo} className="App-logo" alt="Tyrolit Logo" />
      </div>
      <Card>
        {user && materials && customer && (
          <div className="infotext">
            <div className="material-header">

              <div className="material-number">{materials.CustomerPartNumber}</div>
            </div>
            
            <div className="details-section">
              <div className="detail-row">
                <span className="detail-label">Tyrolit PN:</span>
                <span className="detail-value">{searchTerm}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Location:</span>
                <span className="detail-value">{materials.StorageLocation}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Specification:</span>
                <span className="detail-value">{materials.Specification}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Shape:</span>
                <span className="detail-value">{materials.Shape}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Dimensions:</span>
                <span className="detail-value">{materials.Dim_Value}</span>
              </div>
            </div>

            <div className="NumberItemContainer">
              <div className="NumberItem">
                <p className="cardTitle">Shelf Level</p>
                <p className="bigNumber">{materials.Shelf_Level_Actual}</p>
              </div>
              <div className="NumberItem">
                <p className="cardTitle">Restock Level</p>
                <p className="bigNumber">{materials.Restock_Level}</p>
              </div>
            </div>

            <div className="booking-section">
              <div className="quantity-input-group">
                <label htmlFor="quantityInput" className="input-label">Quantity:</label>
                <input
                  id="quantityInput"
                  className="inputspaceA"
                  type="number"
                  value={bookingValue}
                  onChange={(e) => setBookingValue(e.target.value)}
                  placeholder="1"
                  min="1"
                  max="10000"
                  disabled={loading}
                />
              </div>
              {error && (
                <div style={{
                  padding: "12px",
                  backgroundColor: "#fee",
                  color: "#c33",
                  borderRadius: "6px",
                  fontSize: "14px",
                  marginBottom: "12px"
                }}>
                  {error}
                </div>
              )}
              <div className="button-group">
                <Button 
                  className="buttonspaceA buttonIn" 
                  type="button" 
                  onClick={() => handleBooking(true)}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "📥 Book In"}
                </Button>
                <Button 
                  className="buttonspaceA buttonOut" 
                  type="button" 
                  onClick={() => handleBooking(false)}
                  disabled={loading}
                >
                  {loading ? "Processing..." : "📤 Book Out"}
                </Button>
              </div>
              <p className="helper-text">
                💡 Book In increases shelf stock, Book Out decreases it
              </p>
            </div>

            <div className="user-info-section">
              <span className="emailtext">👤 {user.attributes.email}</span>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                <Button 
                  className="buttonspaceA" 
                  onClick={() => (window.location.href = "/view-all-stock")}
                  style={{ marginBottom: 0 }}
                >
                  View All Stock
                </Button>
                <Button 
                  className="buttonspaceA" 
                  onClick={signOut}
                  style={{ marginBottom: 0, background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }}
                >
                  Sign Out
                </Button>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default withAuthenticator(MaterialPage, { hideSignUp: true });
